import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, Truck, Banknote, Smartphone, CreditCard, CheckCircle2, ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatFCFA, waLink, CONTACT } from "../lib/constants";
import { createOrder } from "../lib/api";
import GoldDivider from "../components/GoldDivider";
import { toast } from "sonner";

const PAYMENTS = [
  { key: "cash_on_delivery", label: "Paiement à la livraison", desc: "Payez en espèces à la réception", icon: Banknote },
  { key: "mobile_money", label: "Mobile Money", desc: "Orange, MTN, Moov, Wave", icon: Smartphone },
  { key: "card", label: "Carte bancaire", desc: "Paiement sécurisé (à la confirmation)", icon: CreditCard },
];

export default function Checkout() {
  const { items, totalPrice, clearCart } = useCart();
  const navigate = useNavigate();
  const [payment, setPayment] = useState("cash_on_delivery");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    customer_name: "", phone: "", email: "", address: "", city: "", note: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (items.length === 0 && !done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center">
        <h1 className="font-display text-3xl text-[#0A0A0A] mb-4">Votre panier est vide</h1>
        <Link to="/boutique" className="btn-gold px-8 py-4 rounded-sm inline-flex">Aller à la boutique</Link>
      </div>
    );
  }

  const buildWaMessage = (order) =>
    `Bonjour MaxiChoix ! Nouvelle commande *${order.order_number}*\n\n` +
    `👤 ${form.customer_name}\n📞 ${form.phone}\n📍 ${form.address}, ${form.city}\n\n` +
    items.map((i) => `• ${i.name} x${i.quantity} — ${formatFCFA(i.price * i.quantity)}`).join("\n") +
    `\n\n*Total : ${formatFCFA(totalPrice)}*\nPaiement : ${PAYMENTS.find((p) => p.key === payment).label}` +
    (form.note ? `\nNote : ${form.note}` : "");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.phone || !form.address || !form.city) {
      toast.error("Veuillez remplir les champs obligatoires");
      return;
    }
    setSubmitting(true);
    try {
      const order = await createOrder({
        ...form,
        payment_method: payment,
        total: totalPrice,
        items: items.map((i) => ({
          product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, image: i.image,
        })),
      });
      const message = buildWaMessage(order);
      setDone({ order, message });
      clearCart();
      window.open(waLink(message), "_blank");
    } catch {
      toast.error("Une erreur est survenue. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="order-success">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <CheckCircle2 size={72} className="mx-auto text-[#B8860B] mb-6" />
          <h1 className="font-display text-4xl text-[#0A0A0A] mb-3">Commande confirmée !</h1>
          <p className="text-neutral-500 mb-2">Votre numéro de commande</p>
          <p className="font-display text-3xl gold-text mb-8">{done.order.order_number}</p>
          <GoldDivider className="mb-8" />
          <p className="text-neutral-600 mb-8 leading-relaxed">
            Merci {form.customer_name} ! Nous avons bien reçu votre commande. Finalisez-la sur WhatsApp
            pour confirmer votre adresse et le mode de livraison.
          </p>
          <a href={waLink(done.message)} target="_blank" rel="noreferrer" className="btn-gold px-8 py-4 rounded-sm inline-flex mb-4" data-testid="success-whatsapp">
            <MessageCircle size={18} /> Confirmer sur WhatsApp
          </a>
          <div>
            <Link to="/boutique" className="text-sm text-[#B8860B] hover:underline">Retour à la boutique</Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-12" data-testid="checkout-page">
      <h1 className="font-display text-4xl md:text-5xl text-[#0A0A0A] mb-2">Finaliser la commande</h1>
      <p className="text-neutral-500 mb-10">Livraison rapide partout en Côte d'Ivoire</p>

      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_380px] gap-10">
        <div className="space-y-10">
          {/* Coordonnées */}
          <div>
            <h3 className="font-display text-2xl text-[#0A0A0A] mb-5 flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-[#0A0A0A] text-[#F4E5A1] text-sm flex items-center justify-center">1</span>
              Vos coordonnées
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nom complet *" value={form.customer_name} onChange={set("customer_name")} testid="checkout-name" />
              <Field label="Téléphone *" value={form.phone} onChange={set("phone")} testid="checkout-phone" placeholder="Ex : 07 00 00 00 00" />
              <Field label="Email" value={form.email} onChange={set("email")} testid="checkout-email" type="email" className="sm:col-span-2" />
              <Field label="Adresse de livraison *" value={form.address} onChange={set("address")} testid="checkout-address" className="sm:col-span-2" />
              <Field label="Ville / Commune *" value={form.city} onChange={set("city")} testid="checkout-city" />
            </div>
            <div className="mt-4">
              <label className="text-sm text-neutral-600 mb-1 block">Note (facultatif)</label>
              <textarea value={form.note} onChange={set("note")} rows={3}
                className="w-full border border-neutral-200 rounded-sm py-2.5 px-3 text-sm focus:outline-none focus:border-[#B8860B]"
                data-testid="checkout-note" placeholder="Instructions de livraison…" />
            </div>
          </div>

          {/* Paiement */}
          <div>
            <h3 className="font-display text-2xl text-[#0A0A0A] mb-5 flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-[#0A0A0A] text-[#F4E5A1] text-sm flex items-center justify-center">2</span>
              Mode de paiement
            </h3>
            <div className="space-y-3">
              {PAYMENTS.map((p) => (
                <button
                  type="button"
                  key={p.key}
                  onClick={() => setPayment(p.key)}
                  className={`w-full flex items-center gap-4 p-4 border rounded-sm text-left transition ${payment === p.key ? "border-[#B8860B] bg-[#FBF9F4]" : "border-neutral-200 hover:border-neutral-300"}`}
                  data-testid={`payment-${p.key}`}
                >
                  <p.icon size={22} className={payment === p.key ? "text-[#B8860B]" : "text-neutral-400"} />
                  <div className="flex-1">
                    <p className="font-medium text-[#0A0A0A]">{p.label}</p>
                    <p className="text-xs text-neutral-500">{p.desc}</p>
                  </div>
                  <span className={`w-5 h-5 rounded-full border-2 ${payment === p.key ? "border-[#B8860B] bg-[#B8860B]" : "border-neutral-300"}`} />
                </button>
              ))}
            </div>
            <p className="text-xs text-neutral-400 mt-3 flex items-center gap-1">
              <Truck size={14} /> Le paiement en ligne (Mobile Money / carte) sera confirmé via WhatsApp avec un lien sécurisé.
            </p>
          </div>
        </div>

        {/* Summary */}
        <div className="lg:sticky lg:top-28 h-fit bg-[#0A0A0A] text-white rounded-lg p-8">
          <h3 className="font-display text-2xl mb-6">Votre commande</h3>
          <div className="space-y-4 mb-6 max-h-64 overflow-y-auto no-scrollbar">
            {items.map((i) => (
              <div key={i.product_id} className="flex gap-3 items-center">
                <div className="w-14 h-14 bg-white/10 rounded overflow-hidden shrink-0 relative">
                  <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                  <span className="absolute -top-1 -right-1 bg-[#B8860B] text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center">{i.quantity}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{i.name}</p>
                  <p className="text-xs text-neutral-400">{formatFCFA(i.price)}</p>
                </div>
                <span className="text-sm text-[#F4E5A1]">{formatFCFA(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <GoldDivider className="mb-6" />
          <div className="flex justify-between items-baseline mb-8">
            <span className="text-lg">Total</span>
            <span className="font-display text-3xl gold-text">{formatFCFA(totalPrice)}</span>
          </div>
          <button type="submit" disabled={submitting} className="btn-gold w-full py-4 rounded-sm disabled:opacity-60" data-testid="place-order">
            {submitting ? "Traitement…" : <>Confirmer la commande <ArrowRight size={18} /></>}
          </button>
          <p className="text-center text-xs text-neutral-500 mt-4">
            Besoin d'aide ? {CONTACT.phoneIntl}
          </p>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", className = "", placeholder }) {
  return (
    <div className={className}>
      <label className="text-sm text-neutral-600 mb-1 block">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full border border-neutral-200 rounded-sm py-2.5 px-3 text-sm focus:outline-none focus:border-[#B8860B]"
        data-testid={testid}
      />
    </div>
  );
}
